import type { MetaFunction } from '@remix-run/node';
import { Link, useSearchParams } from '@remix-run/react';
import { useQuery } from '@tanstack/react-query';
import {
  ArrowLeft,
  Check,
  Sparkles,
  Palette,
  Megaphone,
  Zap,
  Target,
  Image,
  Microscope,
  Shield,
  RefreshCcw,
  ChevronDown,
} from 'lucide-react';
import { useState } from 'react';

import { PayPalCheckoutButton } from '~/components/billing/PayPalCheckoutButton';
import { get } from '~/lib/api';
import {
  describeBlueprintAccess,
  describePlanAi,
} from '~/lib/plan-descriptors';
import { queryKeys } from '~/lib/query-keys';
import type { BillingPlan, PayPalConfig } from '~/types/billing';

export const meta: MetaFunction = () => [
  { title: 'Checkout — Deleg8 Studio' },
  {
    name: 'description',
    content: 'Complete your purchase and start creating campaigns.',
  },
];

const LIMIT_LABELS = [
  { key: 'maxBrands', label: 'Brand profiles', icon: Palette },
  { key: 'maxCampaigns', label: 'Campaigns', icon: Megaphone },
  { key: 'maxGenerations', label: 'AI generation runs', icon: Zap },
  { key: 'maxCompetitors', label: 'Competitor profiles', icon: Target },
  { key: 'maxImageUploads', label: 'Reference images', icon: Image },
  { key: 'maxDeepAnalyses', label: 'Competitor deep dives', icon: Microscope },
] as const;

export default function PublicCheckoutPage() {
  const [searchParams] = useSearchParams();
  const planKey = searchParams.get('plan');
  const [email, setEmail] = useState('');
  const [emailValid, setEmailValid] = useState(false);
  const [planDetailsOpen, setPlanDetailsOpen] = useState(false);

  const { data: plans = [], isLoading: plansLoading } = useQuery({
    queryKey: queryKeys.billing.plans,
    queryFn: () => get<BillingPlan[]>('/billing/plans'),
    staleTime: 5 * 60 * 1000,
  });

  const { data: paypalConfig } = useQuery({
    queryKey: queryKeys.billing.paypalConfig,
    queryFn: () => get<PayPalConfig | null>('/billing/paypal-config'),
    staleTime: 30 * 60 * 1000,
  });

  const paidPlans = plans.filter((p) => p.key !== 'trial');
  const selectedPlan = paidPlans.find((p) => p.key === planKey);

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setEmail(val);
    setEmailValid(/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val));
  };

  return (
    <div className="min-h-screen bg-surface-950 text-white">
      {/* Nav */}
      <nav className="border-b border-white/[0.06] bg-surface-950/80 backdrop-blur-xl">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-3 sm:px-6 sm:py-4">
          <Link to="/" className="flex items-center gap-2">
            <img
              src="/logo.png"
              alt="Deleg8 Studio"
              className="h-8 w-8"
              loading="lazy"
            />
            <span className="text-lg font-bold">Deleg8 Studio</span>
          </Link>
          <Link
            to="/login"
            className="text-sm font-medium text-white/70 transition hover:text-white"
          >
            Already have an account? Log in
          </Link>
        </div>
      </nav>

      <div className="relative px-4 pb-16 pt-12 sm:px-6 sm:pb-24 sm:pt-16">
        {/* Ambient blobs */}
        <div className="pointer-events-none absolute -left-40 top-20 h-[400px] w-[400px] rounded-full bg-creative-700/20 blur-[100px]" />
        <div className="pointer-events-none absolute -right-32 top-10 h-[350px] w-[350px] rounded-full bg-accent-600/15 blur-[80px]" />

        <div className="relative z-10 mx-auto max-w-4xl">
          {plansLoading ? (
            <div className="flex items-center justify-center py-20">
              <div className="h-8 w-8 animate-spin rounded-full border-2 border-white/20 border-t-creative-400" />
            </div>
          ) : !selectedPlan ? (
            /* No plan selected — show message */
            <div className="text-center">
              <h1 className="text-2xl font-bold sm:text-3xl">
                Choose a plan first
              </h1>
              <p className="mt-3 text-white/60">
                Pick the plan that fits your needs.
              </p>
              <Link
                to="/pricing"
                className="mt-6 inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-creative-600 via-accent-500 to-heat-500 px-6 py-3 text-sm font-semibold text-white shadow-glow transition hover:brightness-110"
              >
                View Plans
              </Link>
            </div>
          ) : (
            <>
              <div className="text-center">
                <h1 className="text-2xl font-bold sm:text-3xl">
                  Complete your purchase
                </h1>
                <p className="mt-2 text-sm text-white/60">
                  Enter your email and pay — we&apos;ll send you a link to set up
                  your account.
                </p>
              </div>

              <div className="mt-10 grid gap-8 lg:grid-cols-2">
                {/* Plan summary — collapsible on mobile, second column on desktop */}
                <div className="order-2 lg:order-1 rounded-2xl border border-white/[0.08] bg-white/[0.03] p-6 backdrop-blur-sm sm:p-8">
                  {/* Always-visible header */}
                  <div className="flex items-center justify-between">
                    <div>
                      <h2 className="text-lg font-semibold">
                        {selectedPlan.name}
                      </h2>
                      <div className="mt-1 flex items-baseline gap-1">
                        <span className="text-3xl font-bold">
                          ${selectedPlan.price}
                        </span>
                        <span className="text-sm text-white/50">/mo</span>
                      </div>
                    </div>
                    <Link
                      to="/pricing"
                      className="text-sm font-medium text-creative-400 transition hover:text-creative-300"
                    >
                      Change plan
                    </Link>
                  </div>

                  {/* Toggle on mobile, always visible on desktop */}
                  <button
                    type="button"
                    onClick={() => setPlanDetailsOpen((v) => !v)}
                    className="mt-3 flex items-center gap-1 text-xs font-medium text-white/40 lg:hidden"
                  >
                    {planDetailsOpen ? 'Hide' : 'View'} plan details
                    <ChevronDown className={`h-3.5 w-3.5 transition ${planDetailsOpen ? 'rotate-180' : ''}`} />
                  </button>

                  <div className={`mt-4 ${planDetailsOpen ? '' : 'hidden'} lg:block`}>
                    <div className="flex items-center gap-2 rounded-lg bg-white/[0.06] px-3 py-2">
                      <Sparkles className="h-4 w-4 text-creative-400" />
                      <span className="text-xs font-medium text-white/70">
                        AI: {describePlanAi(selectedPlan)}
                      </span>
                    </div>

                    <ul className="mt-5 space-y-2.5">
                      {LIMIT_LABELS.map(({ key, label, icon: Icon }) => {
                        const value =
                          selectedPlan.limits[
                            key as keyof typeof selectedPlan.limits
                          ];
                        return (
                          <li
                            key={key}
                            className="flex items-center gap-2.5 text-sm"
                          >
                            <Icon className="h-4 w-4 flex-shrink-0 text-white/30" />
                            <span className="text-white/70">{label}</span>
                            <span className="ml-auto font-semibold">
                              {typeof value === 'number' ? value : '--'}
                            </span>
                          </li>
                        );
                      })}
                      <li className="flex items-center gap-2.5 text-sm">
                        <Check className="h-4 w-4 flex-shrink-0 text-emerald-400" />
                        <span className="text-white/70">
                          {describeBlueprintAccess(
                            selectedPlan.limits.allowedBlueprintKeys,
                          )}
                        </span>
                      </li>
                    </ul>
                  </div>
                </div>

                {/* Email + Payment — first on mobile */}
                <div className="order-1 lg:order-2 rounded-2xl border border-white/[0.08] bg-white/[0.03] p-6 backdrop-blur-sm sm:p-8">
                  <h3 className="text-base font-semibold">
                    Complete payment
                  </h3>
                  <p className="mt-1 text-sm text-white/50">
                    Secure checkout powered by PayPal
                  </p>

                  {/* Email input */}
                  <div className="mt-5">
                    <label
                      htmlFor="checkout-email"
                      className="block text-sm font-medium text-white/70"
                    >
                      Email address
                    </label>
                    <input
                      id="checkout-email"
                      type="email"
                      value={email}
                      onChange={handleEmailChange}
                      placeholder="you@example.com"
                      className="mt-1.5 w-full rounded-xl border border-white/[0.12] bg-white/[0.06] px-4 py-2.5 text-sm text-white placeholder-white/30 outline-none transition focus:border-creative-500/50 focus:ring-1 focus:ring-creative-500/30"
                    />
                  </div>

                  {/* PayPal button */}
                  <div className="mt-6">
                    {paypalConfig &&
                    selectedPlan.paypalPlanId &&
                    emailValid ? (
                      <PayPalCheckoutButton
                        planId={selectedPlan.id}
                        paypalPlanId={selectedPlan.paypalPlanId}
                        paypalConfig={paypalConfig}
                        publicCheckout={{ email }}
                      />
                    ) : (
                      <div className="rounded-xl border border-white/[0.08] bg-white/[0.04] px-4 py-3.5 text-center text-sm text-white/40">
                        {!emailValid
                          ? 'Enter your email to continue'
                          : 'Setting up payment...'}
                      </div>
                    )}
                  </div>

                  {/* Trust signals */}
                  <div className="mt-5 space-y-2.5">
                    <div className="flex items-center gap-2 text-xs text-white/40">
                      <RefreshCcw className="h-3.5 w-3.5" />
                      <span>Billed monthly. Cancel anytime — no questions asked.</span>
                    </div>
                    <div className="flex items-center gap-2 text-xs text-white/40">
                      <Shield className="h-3.5 w-3.5" />
                      <span>256-bit SSL encryption. Your payment info is never stored on our servers.</span>
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}

          {/* Back link */}
          <div className="mt-12 text-center">
            <Link
              to="/pricing"
              className="inline-flex items-center gap-1.5 text-sm text-white/50 transition hover:text-white/80"
            >
              <ArrowLeft className="h-4 w-4" />
              Back to pricing
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
